import { has } from "lodash";
import { from, Observable, of } from "rxjs";
import { store } from "..";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { BillingActions } from "../billing/actions";
import { BillingDTO } from "../billing/models/dto";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { processErrorFromPromise } from "../errors/utils";
import { ChainActions } from "./actions";
import { ChainDTO } from "./models/dto";

export namespace ChainService {
  const api = new API("chain");

  export function postWalletReport(
    report: ChainDTO.Requests.PostWalletCheck.T
  ): Promise<ChainDTO.Responses.GetSingleWalletReports.T> {
    return api
      .post(`wallets/check`)
      .send(report)
      .then<ChainDTO.Responses.GetSingleWalletReports.T>((response) => {
        store.dispatch(ChainActions.resetAllWalletReports());
        store.dispatch(BillingActions.resetAllUsages());
        store.dispatch(BillingActions.resetBalance());
        store.dispatch(BillingActions.resetChainBilling());
        return response.body;
      })
      .catch<ChainDTO.Responses.GetSingleWalletReports.T>(
        processErrorFromPromise
      );
  }

  export function getAllWalletReports(
    pagination: Pageable.T<
      Exclude<keyof ChainDTO.Responses.GetSingleWalletReports.T, "riskProfile">
    > = {}
  ): Observable<
    FetchableError | FetchableSuccess<ChainDTO.Responses.GetAllWalletReports.T>
  > {
    return from(
      api
        .get(`wallets/profiles`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<ChainDTO.Responses.GetAllWalletReports.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleWalletReports(
    id: ChainDTO.Responses.GetSingleWalletReports.T["id"]
  ): Promise<ChainDTO.Responses.GetSingleWalletReports.T> {
    const { singleWalletReportsMap } = store.getState().chain;

    if (
      has(singleWalletReportsMap, id) &&
      singleWalletReportsMap[id] !== "LOADING"
    ) {
      return Promise.resolve(
        //TOOD fix for concurrency
        singleWalletReportsMap[
          id
        ] as ChainDTO.Responses.GetSingleWalletReports.T
      );
    }

    store.dispatch(ChainActions.getSingleWalletReports(id));
    return api
      .get(`v1/wallets/check/${id}`)
      .then<ChainDTO.Responses.GetSingleWalletReports.T>((response) => {
        store.dispatch(
          ChainActions.setSingleWalletReports({
            id,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<ChainDTO.Responses.GetSingleWalletReports.T>(
        processErrorFromPromise
      );
  }


  // Billing

  export function getBilling(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });

    return from(
      api
        .get(`billing/${login.data.accessToken.credentialsId}/apis`)
        .then<FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }
}
